import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, Row, Col, Spin, Form, Input, Space, Switch, notification } from 'antd';
import { useUserSettingsLogic } from '../useUserSettingsLogic';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';
import './style.less';

const EditUserModal = ({ isVisible, onSaveCallback, onCloseCallback, user }) => {
	const reduxUser = useSelector((state) => state.user);
	const roles = useSelector((state) => state.company?.roles ?? []);

	const userSettingsLogic = useUserSettingsLogic();

	const [form] = Form.useForm();
	const [isEditingSelf, setIsEditingSelf] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [areFormErrors, setAreFormErrors] = useState(false);

	const { t } = useI18nLocalization();

	const [userRoleIds, setUserRoleIds] = useState([]);

	useEffect(() => {
		if (reduxUser && user) {
			setUser();
			setIsEditingSelf(reduxUser.profile.userId === user.profile.userId);
		}
	}, [reduxUser?.profile?.userId, user?.profile.userId, isVisible]);

	const setUser = () => {
		setUserRoleIds(user.roles.map((r) => r.roleId));
	};

	const getUpdatedUser = (formValues) => {
		const updatedUserObj = {
			userId: user.profile.userId,
			roleIds: userRoleIds,
			userEnabled: formValues.isActive
		};
		return updatedUserObj;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const updatedUser = getUpdatedUser(formValues);
		const successful = await userSettingsLogic.updateUser(updatedUser);
		if (successful) {
			notification['success']({
				message: isEditingSelf
					? t(`User saved! You'll need to refresh for your changes to take effect.`)
					: t(`User saved!`)
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t(
					`We encountered an error saving this user. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`
				),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
		setUserRoleIds([]);
	};

	return (
		<>
			<Modal
				title={t('Edit User')}
				visible={isVisible}
				okText={t('Save')}
				onOk={() => {
					//-- Will trigger the form's onFinish event
					form.submit();
				}}
				okButtonProps={{ disabled: areFormErrors }}
				confirmLoading={isSaving}
				onCancel={handleOnCancel}
				cancelText={t('Cancel')}
			>
				{!user?.profile.userId ? (
					<Row justify='center' align='middle' style={{ height: '100%' }}>
						<Col>
							<Spin />
						</Col>
					</Row>
				) : (
					<Form
						form={form}
						onFieldsChange={() => {
							const areErrors = form.getFieldsError().some((fe) => fe.errors.length > 0);
							setAreFormErrors(areErrors);
						}}
						layout='vertical'
						requiredMark
						scrollToFirstError
						onFinish={handleOnSave}
					>
						<Row gutter={[16, 0]}>
							<Col span={24} lg={{ span: 12 }}>
								<Form.Item label={t('First Name')}>
									<Input
										disabled
										className='darken-disabled-text'
										placeholder={t('first name')}
										value={user.profile.firstName}
									/>
								</Form.Item>
							</Col>
							<Col span={24} lg={{ span: 12 }}>
								<Form.Item label={t('Last Name')}>
									<Input
										disabled
										className='darken-disabled-text'
										placeholder={t('last name')}
										value={user.profile.lastName}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Form.Item label={t('Email Address')}>
							<Input
								disabled
								className='darken-disabled-text'
								placeholder={t('email address')}
								value={user.profile.emailAddress}
							/>
						</Form.Item>
						<Form.Item label={t('Employee Id')}>
							<Input
								disabled
								className='darken-disabled-text'
								placeholder={t('employee id')}
								value={user.profile.employeeId}
							/>
						</Form.Item>
						<Form.Item label='OID'>
							<Input disabled className='darken-disabled-text' placeholder='oid' value={user.profile.oid} />
						</Form.Item>
						<Form.Item label={t('Security Id')}>
							<Input
								disabled
								className='darken-disabled-text'
								placeholder={t('security id')}
								value={user.profile.objectSid}
							/>
						</Form.Item>
						<Form.Item label={t('Job Title')}>
							<Input
								disabled
								className='darken-disabled-text'
								placeholder={t('job title')}
								value={user.profile.jobTitle}
							/>
						</Form.Item>
						<Form.Item
							name='roles'
							label={t('Roles')}
							required
							rules={[
								{
									validator: () => {
										let adminRoles = APP_SETTINGS.adminRoles;
										adminRoles = adminRoles.map((ar) => ar.toLowerCase());

										const adminRoleObjects = roles.filter((r) => adminRoles.includes(r.name.toLowerCase()));

										const currentNumAdminRoles = reduxUser.roles.filter((r) =>
											adminRoles.includes(r.role.toLowerCase())
										).length;
										const newNumAdminRoles = adminRoleObjects.filter((aro) => userRoleIds.includes(aro.id)).length;

										if (isEditingSelf && currentNumAdminRoles > newNumAdminRoles) {
											return Promise.reject(new Error(t('You cannot remove an admin role from yourself')));
										}

										if (userRoleIds.length === 0) {
											return Promise.reject(new Error(t('A user must have at least one role')));
										}

										return Promise.resolve();
									}
								}
							]}
						>
							<RoleList
								roles={roles}
								userRoleIds={userRoleIds}
								setUserRoleIds={setUserRoleIds}
								onChange={() => {
									return null;
								}}
							/>
						</Form.Item>
						<Form.Item
							name='isActive'
							label={t('Active')}
							required
							valuePropName='checked'
							initialValue={user.profile.userEnabled}
							rules={[
								{
									validator: (field, value) => {
										if (isEditingSelf && !value) {
											return Promise.reject(new Error(t('You cannot deactivate yourself')));
										}

										return Promise.resolve();
									}
								}
							]}
						>
							<Switch />
						</Form.Item>
					</Form>
				)}
			</Modal>
		</>
	);
};

EditUserModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	onCloseCallback: PropTypes.func.isRequired,
	user: PropTypes.object
};

//-- Need to do this beceause the validator requires an onChange event be on the top-most element of Form.Input
const RoleList = ({ roles, userRoleIds, setUserRoleIds, onChange }) => {
	const sortedRoles = _.orderBy(roles, ['name'], ['asc']);
	const { t } = useI18nLocalization();
	return (
		<Space direction='vertical'>
			{_.map(sortedRoles, (sr) => {
				return (
					<Space key={sr.id} direction='horizontal'>
						<Switch
							checked={userRoleIds.includes(sr.id)}
							onChange={(newValue) => {
								//-- Adding the role
								if (newValue) {
									setUserRoleIds(userRoleIds.concat([sr.id]));
									onChange();
								}
								//-- Removing the role
								else {
									setUserRoleIds(userRoleIds.filter((urid) => urid !== sr.id));
									onChange();
								}
							}}
						/>
						<span>
							{sr.name} <i>({t(sr.description)})</i>
						</span>
					</Space>
				);
			})}
		</Space>
	);
};

RoleList.propTypes = {
	roles: PropTypes.array.isRequired,
	userRoleIds: PropTypes.array.isRequired,
	setUserRoleIds: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired
};

export default EditUserModal;
